import React, { useState, useEffect } from "react";

// Shared global navigation contract — used by Nav.jsx and Footer.jsx.
// Every link uses same-tab navigation to preserve a joined-up journey
// across the katlastechnology subdomains.
export const GLOBAL_NAV_LINKS = [
  { href: "https://www.katlastechnology.com/", label: "KATLAS Technology" },
  { href: "/", label: "Platform" },
  { href: "https://labs.katlastechnology.com/", label: "Cierge Labs" },
  { href: "https://labs.katlastechnology.com/journeys", label: "Journey Storybooks" },
  { href: "https://www.katlastechnology.com/contact/", label: "Contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#020B14]/85 backdrop-blur-xl border-b border-white/[0.06]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a
          href="/"
          data-testid="logo-wordmark"
          className="flex items-center gap-2.5 group"
          aria-label="KATLAS"
        >
          <img
            src="/katlas-mark.png"
            alt=""
            aria-hidden="true"
            className="h-7 w-auto"
            data-testid="logo-mark"
          />
          <span className="font-display tracking-[0.18em] text-sm font-semibold text-white">
            KATLAS
          </span>
          <span className="hidden xl:inline text-[10px] mono uppercase tracking-[0.2em] text-slate-500 ml-2 border-l border-white/10 pl-2">
            Governance by Design
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-0.5">
          {GLOBAL_NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="px-2.5 py-2 text-[12px] text-slate-300 hover:text-white transition-colors whitespace-nowrap"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="https://authority-journey.katlastechnology.com/"
            data-testid="nav-cta-worked-storybook"
            className="cta-primary inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#0052CC] text-white px-3.5 py-2 text-[12px] font-medium rounded-sm whitespace-nowrap"
          >
            See a worked Storybook
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 17 17 7M8 7h9v9"/></svg>
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-slate-300 hover:text-white"
          aria-label="Toggle menu"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {open ? <path d="M6 6l12 12M6 18L18 6"/> : <><path d="M3 6h18"/><path d="M3 12h18"/><path d="M3 18h18"/></>}
          </svg>
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#020B14] border-t border-white/[0.06]">
          <div className="px-6 py-4 flex flex-col gap-3">
            {GLOBAL_NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-slate-200 text-sm py-2 border-b border-white/[0.04]"
              >
                {l.label}
              </a>
            ))}
            <a
              href="https://authority-journey.katlastechnology.com/"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-cta-worked-storybook"
              className="bg-[#0066FF] text-white text-sm py-2.5 text-center mt-2 rounded-sm"
            >
              See a worked Storybook
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
